import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Type,
  Package,
  Hash,
  Layers,
  ChevronDown,
  Search,
  X,
  Check,
} from "lucide-react";
import { CampaignType } from "@/@types/campaign";
import { SearchType } from "../ducks/filter/types";

interface SearchOption {
  value: SearchType;
  label: string;
  shortLabel: string;
  icon: React.ElementType;
}

const ALL_SEARCH_OPTIONS: SearchOption[] = [
  { value: "name", label: "Nome da campanha", shortLabel: "Nome", icon: Type },
  { value: "sku", label: "Código SKU", shortLabel: "SKU", icon: Package },
  { value: "campaignCode", label: "Código de campanha", shortLabel: "Código", icon: Hash },
  { value: "department", label: "Departamento", shortLabel: "Depto", icon: Layers },
];

interface CampaignSearchInputProps {
  searchType: SearchType;
  searchValue: string;
  campaignTypes: CampaignType[];
  onSearchTypeChange: (type: SearchType) => void;
  onSearchValueChange: (value: string) => void;
  onSearch: () => void;
  onClear: () => void;
}

export function CampaignSearchInput({
  searchType,
  searchValue,
  campaignTypes,
  onSearchTypeChange,
  onSearchValueChange,
  onSearch,
  onClear,
}: CampaignSearchInputProps) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastAppliedRef = useRef(searchValue);

  const stableSearch = useCallback(() => {
    if (lastAppliedRef.current !== searchValue) {
      lastAppliedRef.current = searchValue;
      onSearch();
    }
  }, [searchValue, onSearch]);

  useEffect(() => {
    lastAppliedRef.current = searchValue;
  }, [searchType]);

  const availableOptions = useMemo(() => {
    return ALL_SEARCH_OPTIONS.filter((option) => {
      if (option.value === "name" || option.value === "campaignCode") return true;
      if (option.value === "sku") {
        return campaignTypes.some(
          (t) => t === CampaignType.PRO014 || t === CampaignType.PRO017
        );
      }
      if (option.value === "department") {
        return campaignTypes.some((t) => t === CampaignType.PRO013);
      }
      return false;
    });
  }, [campaignTypes]);

  const currentOption = ALL_SEARCH_OPTIONS.find((o) => o.value === searchType) || ALL_SEARCH_OPTIONS[0];

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        lastAppliedRef.current = ""; // force apply
        stableSearch();
      }
    },
    [stableSearch]
  );

  const handleSelectType = useCallback(
    (type: SearchType) => {
      onSearchTypeChange(type);
      setOpen(false);
      inputRef.current?.focus();
    },
    [onSearchTypeChange]
  );

  const IconComponent = currentOption.icon;

  return (
    <div className="flex items-center">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-8 rounded-r-none border-r-0 gap-1 px-2.5 text-xs font-normal"
          >
            <IconComponent className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="hidden sm:inline">{currentOption.shortLabel}</span>
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-52 p-1" align="start">
          {availableOptions.map((option) => {
            const Icon = option.icon;
            const isActive = option.value === searchType;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelectType(option.value)}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
              >
                <Icon className="h-4 w-4 text-muted-foreground" />
                <span className="flex-1 text-left">{option.label}</span>
                {isActive && <Check className="h-4 w-4" />}
              </button>
            );
          })}
        </PopoverContent>
      </Popover>
      <div className="relative flex items-center">
        <Search className="absolute left-2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          placeholder="Buscar..."
          value={searchValue}
          onChange={(e) => onSearchValueChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={stableSearch}
          className="h-8 w-[150px] lg:w-[200px] rounded-l-none pl-7 pr-7"
        />
        {searchValue && (
          <button
            type="button"
            onClick={onClear}
            className="absolute right-1.5 p-0.5 rounded-sm hover:bg-muted"
          >
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        )}
      </div>
    </div>
  );
}
